import type { AuditLogEntry } from './types';
import { createIllusion } from '@vielzeug/illusionist';
import { en } from '@vielzeug/illusionist/locales';
import { Temporal } from '@vielzeug/tempo';

type Seed = Omit<AuditLogEntry, 'expiresAt'> & { expiresAt?: string | null };

const seed = (seeds: readonly Seed[]): AuditLogEntry[] =>
  seeds.map(s => ({
    expiresAt: null,
    ...s,
  }));

const g = (
  n: number,
  eventType: string,
  eventBy: string,
  eventAt: string,
  event: Record<string, unknown>,
): Seed => ({
  id: `evt-${n.toString().padStart(4, '0')}`,
  createdAt: eventAt,
  expiresAt: null,
  eventType,
  eventAt,
  eventBy,
  event,
});

const CURATED_FIXTURES: readonly AuditLogEntry[] = seed([
  // --- GitHub Organization Manager -------------------------------------------
  g(1, 'Permission Granted', 'pidD9A2', '2026-10-07T14:35:21Z', {
    success: true,
    action: 'Granted organization owner permission',
    resource: { name: 'odp-internal', type: 'Organization' },
    role: { old: null, new: 'owner' },
    ip: '10.42.15.23',
    organization: 'odp-internal',
  }),
  g(2, 'Permission Revoked', 'pidD9A2', '2026-10-07T14:33:10Z', {
    success: true,
    action: 'Revoked organization owner permission',
    resource: { name: 'odp-internal', type: 'Organization' },
    role: { old: 'owner', new: null },
    organization: 'odp-internal',
  }),
  g(3, 'Workflow Changed', 'github-actions[bot]', '2026-10-06T19:10:45Z', {
    success: true,
    action: 'Updated workflow file',
    resource: { name: 'org-permissions.yml', type: '.github/workflows' },
    repository: 'odp-internal/odp-portal',
    commit: 'a1b2c3d',
  }),
  g(4, 'Permission Granted', 'pidF3B7', '2026-10-05T21:05:11Z', {
    success: false,
    error: 'SAML assertion expired',
    action: 'Granted repository admin permission',
    resource: { name: 'odp-portal', type: 'Repository' },
    role: { old: null, new: 'admin' },
    organization: 'odp-internal',
  }),
  g(5, 'Permission Revoked', 'pidF3B7', '2026-10-05T18:47:02Z', {
    success: true,
    action: 'Revoked repository admin permission',
    resource: { name: 'odp-portal', type: 'Repository' },
    role: { old: 'admin', new: null },
    organization: 'odp-internal',
  }),
  g(6, 'Workflow Run', 'github-actions[bot]', '2026-10-05T12:22:33Z', {
    success: true,
    action: 'Workflow run started',
    resource: { name: 'permission-cleanup', type: 'Workflow' },
    runId: 482103,
    repository: 'odp-internal/odp-portal',
  }),
  g(7, 'Permission Granted', 'pidC8K1', '2026-10-04T16:05:14Z', {
    success: true,
    action: 'Granted organization member permission',
    resource: { name: 'odp-external', type: 'Organization' },
    role: { old: null, new: 'member' },
    organization: 'odp-external',
  }),
  g(8, 'Team Membership Changed', 'pidA1B2', '2026-10-03T09:12:00Z', {
    success: true,
    action: 'Added user to platform-team',
    resource: { name: 'platform-team', type: 'Team' },
    member: { old: null, new: 'j.doe' },
    organization: 'odp-internal',
  }),
  g(9, 'Repository Archived', 'pidD9A2', '2026-10-02T15:40:27Z', {
    success: true,
    action: 'Archived repository',
    resource: { name: 'legacy-service', type: 'Repository' },
    archived: { old: false, new: true },
    organization: 'odp-internal',
  }),
  g(10, 'Permission Granted', 'pidE5G9', '2026-10-01T08:01:55Z', {
    success: false,
    error: 'User not found in identity provider',
    action: 'Granted repository push permission',
    resource: { name: 'odp-gateway', type: 'Repository' },
    role: { old: null, new: 'push' },
    organization: 'odp-internal',
  }),
  g(11, 'Webhook Configured', 'github-actions[bot]', '2026-09-29T13:20:10Z', {
    success: true,
    action: 'Registered organization webhook',
    resource: { name: 'audit-forwarder', type: 'Webhook' },
    url: 'https://audit-forwarder.example.com/hooks/github',
    organization: 'odp-internal',
  }),
  g(12, 'Permission Revoked', 'pidB4H8', '2026-09-25T10:05:00Z', {
    success: true,
    action: 'Revoked team maintainer permission',
    resource: { name: 'design-systems', type: 'Team' },
    role: { old: 'maintainer', new: 'member' },
    organization: 'odp-external',
  }),
  // --- Cost Insights ---------------------------------------------------------
  g(13, 'Budget Updated', 'pidC8K1', '2026-10-07T07:45:00Z', {
    success: true,
    action: 'Raised monthly budget',
    resource: { name: 'odp-portal', type: 'Project' },
    budget: { old: 12000, new: 15000 },
    currency: 'EUR',
  }),
  g(14, 'Alert Rule Changed', 'system:scheduler', '2026-10-06T02:00:00Z', {
    success: true,
    action: 'Recalculated anomaly thresholds',
    resource: { name: 'cost-anomaly', type: 'Alert Rule' },
    threshold: { old: 0.15, new: 0.12 },
  }),
  g(15, 'Access Requested', 'pidF3B7', '2026-10-04T11:11:11Z', {
    success: true,
    action: 'Requested cost dashboard access',
    resource: { name: 'executive-dashboard', type: 'Dashboard' },
    justification: 'Q4 cost review',
  }),
  g(16, 'Budget Updated', 'pidD9A2', '2026-10-02T17:30:40Z', {
    success: false,
    error: 'Budget exceeds department ceiling',
    action: 'Raised monthly budget',
    resource: { name: 'odp-gateway', type: 'Project' },
    budget: { old: 8000, new: 25000 },
    currency: 'EUR',
  }),
  g(17, 'Report Exported', 'pidA1B2', '2026-09-30T14:00:00Z', {
    success: true,
    action: 'Exported monthly cost report',
    resource: { name: 'sep-2026', type: 'Report' },
    format: 'csv',
    rows: 1284,
  }),
  g(18, 'Alert Rule Changed', 'pidE5G9', '2026-09-26T09:50:31Z', {
    success: true,
    action: 'Disabled cost alert',
    resource: { name: 's3-spike', type: 'Alert Rule' },
    enabled: { old: true, new: false },
  }),
  // --- Access Portal (archived producer, older events) -----------------------
  g(19, 'Access Granted', 'pidB4H8', '2026-08-11T12:00:00Z', {
    success: true,
    action: 'Granted application access',
    resource: { name: 'travel-booking', type: 'Application' },
    scope: { old: null, new: 'read' },
  }),
  g(20, 'Access Revoked', 'system:offboarding', '2026-08-01T03:15:00Z', {
    success: true,
    action: 'Revoked all access on offboarding',
    resource: { name: 'j.roe', type: 'User' },
    scope: { old: 'write', new: null },
  }),
]);

const illusion = createIllusion({
  seed: 'audit-log-fixtures-2026',
  locale: en,
});
const GENERATED_EVENT_TYPES = [
  'Permission Granted',
  'Permission Revoked',
  'Workflow Changed',
  'Access Requested',
  'Budget Updated',
] as const;
const GENERATED_RESOURCES = [
  { name: 'odp-internal', type: 'Organization' },
  { name: 'odp-portal', type: 'Repository' },
  { name: 'cost-anomaly', type: 'Alert Rule' },
  { name: 'platform-team', type: 'Team' },
] as const;
const FIXTURE_EPOCH = Temporal.Instant.from('2026-10-08T12:00:00Z');

const GENERATED_FIXTURES = Array.from({ length: 15 }, (_, index) => {
  const failed = index % 6 === 0;
  const resource = GENERATED_RESOURCES[index % GENERATED_RESOURCES.length];
  const eventAt = FIXTURE_EPOCH.subtract({
    hours: (index + 1) * 24 + ((index * 3) % 24),
  }).toString();

  return g(
    index + CURATED_FIXTURES.length + 1,
    GENERATED_EVENT_TYPES[index % GENERATED_EVENT_TYPES.length],
    illusion.person.fullName(),
    eventAt,
    {
      success: !failed,
      ...(failed ? { error: 'Permission check rejected the request' } : {}),
      action: failed
        ? 'Attempted privileged action'
        : 'Updated access settings',
      resource,
      organization: 'odp-internal',
      actorIp: illusion.internet.ip(),
      role: { old: 'member', new: 'owner' },
    },
  );
});

export const AUDIT_LOG_FIXTURES: readonly AuditLogEntry[] = [
  ...CURATED_FIXTURES,
  ...seed(GENERATED_FIXTURES),
];
